/**
 * 控制台 API 客户端。
 *
 * 后端错误是 RFC 9457 `application/problem+json`，这里统一翻译成一个
 * 带 `code` 的 Error —— **UI 永远不该直接展示原始 JSON**，
 * 但排障时又需要拿到 request id，所以两者都保留。
 */

import { getIdToken } from './auth';

/**
 * API 根地址，例如 `https://api.squawkquant.com`。
 *
 * ⚠️ **生产构建必须设置 `VITE_API_BASE`。**
 *
 * 不设置时它是空串，请求会打到控制台自己的域名 —— 而 Cloudflare Pages 的
 * SPA 回退规则（`public/_redirects` 里的 `/* /index.html 200`）会把
 * `/console/v1/me` 也返回成**首页 HTML 加 HTTP 200**。于是 `res.ok` 为真、
 * `res.json()` 抛一个语法错误，用户看到的是「Unexpected token '<'」这类
 * 与真实原因毫无关系的报错。
 *
 * `npm run build` 里有一道检查会在缺这个变量时直接失败（见 package.json），
 * 这里再兜一层运行时的可读错误。
 */
const BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/+$/, '');

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getIdToken();
  if (!token) {
    throw new ApiError('登录已过期，请重新登录', 401, 'unauthenticated');
  }

  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...init.headers,
    },
  });

  if (!res.ok) {
    let detail = `请求失败（${res.status}）`;
    let code = 'unknown';
    let requestId: string | undefined;
    try {
      const problem = await res.json();
      // type 形如 https://docs.../errors/rate_limited —— 取末段作为 code。
      code = String(problem.type ?? '').split('/').pop() || 'unknown';
      detail = problem.detail || problem.title || detail;
      requestId = problem.instance;
    } catch {
      /* 非 JSON 响应（网关错误页等），用默认文案 */
    }
    throw new ApiError(detail, res.status, code, requestId);
  }

  if (res.status === 204) return undefined as T;

  // 即便状态是 2xx，也可能拿到 HTML（网关错误页、SPA 回退）。
  // 直接 res.json() 会抛一个看不出原因的语法错误。
  const contentType = res.headers.get('Content-Type') ?? '';
  if (!contentType.includes('json')) {
    throw new ApiError(
      BASE
        ? `API 返回了非 JSON 响应（${contentType || '无 Content-Type'}），` +
          '请检查 API 地址与网关配置'
        : '控制台未配置 API 地址（VITE_API_BASE），请求打到了自身域名',
      res.status,
      'invalid_response',
    );
  }
  return (await res.json()) as T;
}

// ---- 类型 ------------------------------------------------------------------

export interface Me {
  email: string;
  plan: string;
  entitlement_status: string;
  api_access: boolean;
  access_state: string;
  access_detail: string;
  trial_until: string | null;
  is_admin: boolean;
  limits: {
    requests_per_minute: number;
    requests_per_day: number;
    symbols_per_request: number;
    max_lookback_days: number | null;
    max_keys: number;
  } | null;
}

export interface ApiKey {
  key_id: string;
  masked: string;
  label: string;
  scopes: string[];
  ip_allowlist: string[];
  status: 'active' | 'revoked' | 'suspended';
  created_at: string;
  last_used_at: string | null;
  last_used_ip: string | null;
  rotation_grace_until: string | null;
}

export interface UsageDay {
  date: string;
  requests: number;
  rows_returned: number;
  rate_limited: number;
  keys: Record<string, { label: string; requests: number }>;
}

interface Envelope<T> {
  data: T;
  meta: Record<string, unknown>;
}

// ---- 端点 ------------------------------------------------------------------

export const api = {
  me: () => request<Envelope<Me>>('/console/v1/me').then((r) => r.data),

  listKeys: () =>
    request<Envelope<ApiKey[]>>('/console/v1/keys').then((r) => r.data),

  createKey: (body: { label: string; scopes: string[]; ip_allowlist: string[] }) =>
    request<Envelope<{ key_id: string; api_key: string }>>('/console/v1/keys', {
      method: 'POST',
      body: JSON.stringify(body),
    }).then((r) => r.data),

  rotateKey: (keyId: string, grace: '1h' | '24h' | '7d') =>
    request<Envelope<{ api_key: string; old_key_valid_until: string }>>(
      `/console/v1/keys/${keyId}/rotate`,
      { method: 'POST', body: JSON.stringify({ grace }) },
    ).then((r) => r.data),

  revokeKey: (keyId: string) =>
    request<Envelope<unknown>>(`/console/v1/keys/${keyId}`, { method: 'DELETE' }),

  usage: (days = 30) =>
    request<Envelope<UsageDay[]>>(`/console/v1/usage?days=${days}`).then(
      (r) => r.data,
    ),
};
